// ─────────────────────────────────────────────────────────────────────────────
// Helpers de data compartilhados entre o frontend e o backend.
// ─────────────────────────────────────────────────────────────────────────────

import { format, isBefore, isValid, parse, startOfDay } from 'date-fns';

const DATE_ONLY_FORMAT = 'yyyy-MM-dd';
const BR_DATE_FORMAT = 'dd/MM/yyyy';

/**
 * Formata uma data yyyy-MM-dd (sem hora/timezone) para dd/MM/yyyy. Usa `parse` — nunca
 * `new Date(string)` — pra não deixar o valor cair em UTC e voltar com o dia errado.
 */
export function formatDateOnlyBR(value: string | undefined): string {
	if (!value) return '—';
	return format(parse(value, DATE_ONLY_FORMAT, new Date()), 'dd/MM/yyyy');
}

/**
 * Lê uma data digitada em dd/MM/yyyy. Devolve `null` quando a data não existe (31/02/2026) ou não
 * está completa no formato (1/2/2026). Ano anterior a 1800 também é recusado, como faz o
 * `react-datepicker`, que descarta essas datas.
 */
export function parseTypedDateBR(text: string): Date | null {
	const parsed = parse(text, BR_DATE_FORMAT, new Date());
	if (!isValid(parsed) || parsed.getFullYear() < 1800 || format(parsed, BR_DATE_FORMAT) !== text) return null;
	return parsed;
}

/**
 * Indica se a data final é anterior à inicial. Compara só o dia: várias telas guardam a data
 * final como fim do mês (23:59:59), e o mesmo dia nas duas pontas é um período válido.
 */
export function isDateRangeInverted(startDate: Date | null | undefined, endDate: Date | null | undefined): boolean {
	if (!startDate || !endDate) return false;
	return isBefore(startOfDay(endDate), startOfDay(startDate));
}

// Mensagens do período inválido, as mesmas na tela (DateFilterInput) e na API (MF-1141, MF-1143).
export const INVALID_DATE_MESSAGE = 'Informe uma data válida';
export const END_BEFORE_START_MESSAGE = 'A data final não pode ser anterior à data inicial';
export const MISSING_PERIOD_MESSAGE = 'Informe a data inicial e a data final do período';

/**
 * Indica se o valor é uma data `yyyy-MM-dd` que existe no calendário. `new Date('2026-02-31')` não
 * falha: vira 3 de março. Por isso o formato e o dia são conferidos à parte.
 */
export function isDateOnlyString(value: unknown): value is string {
	if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const parsed = parse(value, DATE_ONLY_FORMAT, new Date());
	return isValid(parsed) && format(parsed, DATE_ONLY_FORMAT) === value;
}

/**
 * Motivo de um período `yyyy-MM-dd` não servir para consulta, ou `null` quando ele serve. Data ausente
 * (`undefined` ou vazia) só é erro quando o período é obrigatório; data presente precisa existir, e o
 * fim não pode ser antes do início.
 */
export function getDateOnlyPeriodError(startDate: unknown, endDate: unknown, { required }: { required: boolean }): string | null {
	const isMissing = (value: unknown) => value === undefined || value === null || value === '';
	if (isMissing(startDate) || isMissing(endDate)) {
		if (required) return MISSING_PERIOD_MESSAGE;
		const informed = isMissing(startDate) ? endDate : startDate;
		return isMissing(informed) || isDateOnlyString(informed) ? null : INVALID_DATE_MESSAGE;
	}
	if (!isDateOnlyString(startDate) || !isDateOnlyString(endDate)) return INVALID_DATE_MESSAGE;
	// `yyyy-MM-dd` em ordem alfabética é a ordem do calendário
	return endDate < startDate ? END_BEFORE_START_MESSAGE : null;
}
